import { useRef, type ChangeEvent, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { brandAssets } from "../assets/brand";
import { tokens } from "../theme";
import { ACCEPTED_FILE_TYPES } from "../lib/attachments";
import { hasNativeFilePicker, postToHost } from "../lib/bridge";

interface AttachButtonProps {
  onAttach?: (files: File[]) => void;
  disabled?: boolean;
  /** Max reached — button stays visible but inert. */
  full?: boolean;
  /** Files that still fit — caps the iOS native picker's selection. */
  remaining?: number;
  style?: CSSProperties;
}

const styles: Record<string, CSSProperties> = {
  button: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    border: "none",
    background: tokens.icon.sparklesBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    flexShrink: 0,
  },
  // Tinted via mask so the glyph follows the config accent colour.
  // Quoted: Vite inlines small SVGs as data: URIs that break a bare url().
  icon: {
    width: 20,
    height: 20,
    backgroundColor: tokens.icon.accent,
    maskImage: `url("${brandAssets.attachment}")`,
    WebkitMaskImage: `url("${brandAssets.attachment}")`,
    maskRepeat: "no-repeat",
    WebkitMaskRepeat: "no-repeat",
    maskPosition: "center",
    WebkitMaskPosition: "center",
    maskSize: "contain",
    WebkitMaskSize: "contain",
  },
};

/**
 * JPG/PNG/WEBP/PDF picker. iOS Safari converts HEIC photos to JPEG because HEIC
 * isn't listed in `accept`. Camera capture is out of scope (BR-15): no `capture`
 * attribute, the Android SDK leaves the camera out of its chooser, and inside
 * the iOS SDK the native picker is used because WKWebView's own menu always
 * offers "Take Photo". Type and size are checked after picking (useAttachments).
 */
export default function AttachButton({
  onAttach,
  disabled,
  full,
  remaining,
  style,
}: AttachButtonProps) {
  const inert = disabled || full;
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    // `accept` is only a picker hint — refused files get a message, not dropped.
    const files = Array.from(event.target.files ?? []);
    // Reset so picking the same file again still fires onChange.
    event.target.value = "";
    if (files.length > 0) onAttach?.(files);
  };

  const openPicker = () => {
    if (hasNativeFilePicker()) {
      // Files come back as FILE_PICKER_RESULT messages (useAvatarWidget).
      postToHost("FILE_PICKER_OPEN", {
        accept: ACCEPTED_FILE_TYPES,
        maxCount: remaining,
        labels: {
          photos: t("input.pickPhotos"),
          files: t("input.pickFiles"),
          cancel: t("input.pickCancel"),
        },
      });
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <>
      <button
        type="button"
        style={{
          ...styles.button,
          opacity: inert ? 0.6 : 1,
          cursor: inert ? "default" : "pointer",
          ...style,
        }}
        onClick={openPicker}
        disabled={inert}
        aria-label={t("input.attach")}
      >
        <span style={styles.icon} aria-hidden="true" />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(",")}
        multiple
        hidden
        onChange={handleFileChange}
      />
    </>
  );
}
